// ===== BEGIN FILE: src/components/report-v2/sections/DashaTimelineSection.tsx =====
/**
 * Dasha timeline section with current periods and visual timeline
 */

import React from 'react';
import { Box, Card, Typography, LinearProgress, Chip } from '@mui/joy';
import { ReportSection } from '../ReportSection';
import type { ReportSectionProps } from '../types';

export const DashaTimelineSection: React.FC<ReportSectionProps> = ({ data, onNavigate }) => {
  const { dashas } = data;

  // Planet colors for visualization
  const planetColors: Record<string, string> = {
    Sun: '#F59E0B',
    Moon: '#94A3B8',
    Mars: '#DC2626',
    Mercury: '#10B981',
    Jupiter: '#FBBF24',
    Venus: '#EC4899',
    Saturn: '#3B82F6',
    Rahu: '#8B5CF6',
    Ketu: '#F97316',
  };

  // Get planet color
  const getPlanetColor = (planet: string): string => {
    return planetColors[planet] || '#6B7280';
  };

  // Format date
  const formatDate = (dateStr: string): string => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // DashaPeriodCard component
  const DashaPeriodCard = ({
    level,
    planet,
    start,
    end,
    percentage,
  }: {
    level: string;
    planet: string;
    start: string;
    end: string;
    percentage: number;
  }) => {
    const color = getPlanetColor(planet);

    return (
      <Card
        variant="outlined"
        sx={{
          p: 1.5,
          borderLeft: '4px solid',
          borderLeftColor: color,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.25 }}>
              {level === 'MD' ? 'Mahadasha' : level === 'AD' ? 'Antardasha' : 'Pratyantar Dasha'}
            </Typography>
            <Typography level="title-md" sx={{ fontWeight: 700, color }}>
              {planet}
            </Typography>
          </Box>
          <Chip size="sm" variant="soft" sx={{ bgcolor: color, color: '#FFF' }}>
            {level}
          </Chip>
        </Box>

        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
              {formatDate(start)}
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
              {formatDate(end)}
            </Typography>
          </Box>
          <LinearProgress
            determinate
            value={percentage}
            sx={{
              '--LinearProgress-thickness': '6px',
              '--LinearProgress-progressThickness': '6px',
              bgcolor: 'neutral.200',
              '& .MuiLinearProgress-progress': {
                bgcolor: color,
              },
            }}
          />
        </Box>

        <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'center' }}>
          {percentage.toFixed(1)}% Complete
        </Typography>
      </Card>
    );
  };

  return (
    <ReportSection
      title="Vimshottari Dasha Timeline"
      icon="⏱️"
      pageBreakBefore
      onNavigate={onNavigate ? () => onNavigate('dasha') : undefined}
      navigateLabel="View Dasha Timeline →"
      navigateTab="dasha"
    >
      <Box sx={{ mb: 3 }}>
        <Typography level="body-md" sx={{ color: 'text.secondary' }}>
          Vimshottari Dasha is a 120-year planetary period system that divides life into specific time
          windows ruled by different planets. Each level (Mahadasha, Antardasha, Pratyantar Dasha) creates
          a unique combination of influences affecting life events.
        </Typography>
      </Box>

      {/* current periods */}
      <Box sx={{ mb: 3 }}>
        <Typography level="title-lg" sx={{ mb: 2, color: 'primary.main' }}>
          📍 Current Active Periods
        </Typography>
        {/* CSS grid forces 3-col in print/PDF */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, rowGap: 2 }}>
          <DashaPeriodCard
            level="MD"
            planet={dashas.current.MD.planet}
            start={dashas.current.MD.start}
            end={dashas.current.MD.end}
            percentage={dashas.current.MD.percentage}
          />
          <DashaPeriodCard
            level="AD"
            planet={dashas.current.AD.planet}
            start={dashas.current.AD.start}
            end={dashas.current.AD.end}
            percentage={dashas.current.AD.percentage}
          />
          <DashaPeriodCard
            level="PD"
            planet={dashas.current.PD.planet}
            start={dashas.current.PD.start}
            end={dashas.current.PD.end}
            percentage={dashas.current.PD.percentage}
          />
        </Box>
      </Box>

      {/* Dasha interpretation — mt:6 ensures gap after the 3-col card grid */}
      {/* MD / AD Detail Cards — mirror the Yogas & Predictions page */}
      {(dashas.current.MD.nakshatra || dashas.current.AD.nakshatra) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 5, mt: 6 }}>
          {/* MD Detail Card */}
          {dashas.current.MD.nakshatra && (() => {
            const md = dashas.current.MD;
            const mdColor = getPlanetColor(md.planet);
            const mdDateRange = `${formatDate(md.start)} — ${formatDate(md.end)}`;
            return (
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid', borderLeftColor: mdColor }}>
                {/* Title */}
                <Typography level="title-lg" sx={{ mb: 0.5, fontWeight: 600, lineHeight: 1.4, fontSize: '1rem' }}>
                  Maha Dasha (Your Life Theme){md.durationStr ? ` (${md.durationStr})` : ''} —{' '}
                  <span style={{ fontWeight: 700, color: mdColor }}>{md.planet}</span>
                  {md.nakshatra && (
                    <> of <span style={{ color: '#b8860b' }}>{md.nakshatra}</span> Nakshatra sitting in <span style={{ color: '#1976d2' }}>{md.zodiacSign}</span></>
                  )}
                </Typography>
                {/* Spotlight */}
                {md.spotlight && (
                  <Typography level="body-sm" sx={{ mb: 0.5, fontWeight: 500 }}>
                    <strong>Spotlight:</strong> {md.spotlight}
                  </Typography>
                )}
                {/* Date range */}
                <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>
                  {mdDateRange}
                </Typography>
                {/* House details */}
                {(md.occupiesHouse !== undefined) && (
                  <Typography level="body-xs" sx={{ mb: 1 }}>
                    Occupies <strong>{md.spotlight}</strong>,{' '}
                    rules <strong>{md.rulesHouses && md.rulesHouses.length > 0 ? `H${md.rulesHouses.join(', H')}` : 'None'}</strong>,{' '}
                    aspects <strong>{md.aspectsHouses && md.aspectsHouses.length > 0 ? `H${md.aspectsHouses.join(', H')}` : 'None'}</strong>
                  </Typography>
                )}
                {/* Transit Analysis */}
                {md.transitAnalysis && (
                  <Box sx={{ mb: 1 }}>
                    <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5 }}>Transit Analysis:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip size="sm" variant="soft" color="success">
                        ✓ {md.transitAnalysis.beneficPct}% benefic support
                      </Chip>
                      <Chip size="sm" variant="soft" color="warning">
                        ⚠ {md.transitAnalysis.maleficPct}% malefic pressure
                      </Chip>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={
                          md.transitAnalysis.transitSupport >= 5 ? 'danger' :
                          md.transitAnalysis.transitSupport >= 3 ? 'primary' : 'neutral'
                        }
                      >
                        {md.transitAnalysis.transitSupport >= 5 ? '🔥' : md.transitAnalysis.transitSupport >= 3 ? '⚡' : '○'} Transit Support {md.transitAnalysis.transitSupport}
                      </Chip>
                    </Box>
                  </Box>
                )}
                {/* Life Focus Areas */}
                {md.lifeFocusAreas && md.lifeFocusAreas.length > 0 && (
                  <Box>
                    <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5 }}>Life Focus Areas:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {md.lifeFocusAreas.slice(0, 6).map(area => (
                        <Chip key={area} size="sm" variant="soft" color="primary">{area}</Chip>
                      ))}
                      {md.lifeFocusAreas.length > 6 && (
                        <Chip size="sm" variant="outlined" color="neutral">+{md.lifeFocusAreas.length - 6} more</Chip>
                      )}
                    </Box>
                  </Box>
                )}
              </Card>
            );
          })()}

          {/* AD Detail Card */}
          {dashas.current.AD.nakshatra && (() => {
            const ad = dashas.current.AD;
            const adColor = getPlanetColor(ad.planet);
            const adDateRange = `${formatDate(ad.start)} — ${formatDate(ad.end)}`;
            return (
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid', borderLeftColor: adColor, bgcolor: 'background.level1' }}>
                {/* Title */}
                <Typography level="title-lg" sx={{ mb: 0.5, fontWeight: 600, lineHeight: 1.4, fontSize: '1rem' }}>
                  Antar Dasha (Medium-Term Theme){ad.durationStr ? ` (${ad.durationStr})` : ''} —{' '}
                  <span style={{ fontWeight: 700, color: adColor }}>{ad.planet}</span>
                  {ad.nakshatra && (
                    <> of <span style={{ color: '#b8860b' }}>{ad.nakshatra}</span> Nakshatra sitting in <span style={{ color: '#1976d2' }}>{ad.zodiacSign}</span></>
                  )}
                </Typography>
                {/* Spotlight */}
                {ad.spotlight && (
                  <Typography level="body-sm" sx={{ mb: 0.5, fontWeight: 500 }}>
                    <strong>Spotlight:</strong> {ad.spotlight}
                  </Typography>
                )}
                {/* Date range */}
                <Typography level="body-xs" sx={{ mb: 0.5, color: 'text.secondary' }}>
                  {adDateRange}
                </Typography>
                {/* House details */}
                {(ad.occupiesHouse !== undefined) && (
                  <Typography level="body-xs" sx={{ mb: 1 }}>
                    Occupies <strong>{ad.spotlight}</strong>,{' '}
                    rules <strong>{ad.rulesHouses && ad.rulesHouses.length > 0 ? `H${ad.rulesHouses.join(', H')}` : 'None'}</strong>,{' '}
                    aspects <strong>{ad.aspectsHouses && ad.aspectsHouses.length > 0 ? `H${ad.aspectsHouses.join(', H')}` : 'None'}</strong>
                  </Typography>
                )}
                {/* Transit Analysis */}
                {ad.transitAnalysis && (
                  <Box sx={{ mb: 1 }}>
                    <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5 }}>Transit Analysis:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip size="sm" variant="soft" color="success">
                        ✓ {ad.transitAnalysis.beneficPct}% benefic support
                      </Chip>
                      <Chip size="sm" variant="soft" color="warning">
                        ⚠ {ad.transitAnalysis.maleficPct}% malefic pressure
                      </Chip>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={
                          ad.transitAnalysis.transitSupport >= 5 ? 'danger' :
                          ad.transitAnalysis.transitSupport >= 3 ? 'primary' : 'neutral'
                        }
                      >
                        {ad.transitAnalysis.transitSupport >= 5 ? '🔥' : ad.transitAnalysis.transitSupport >= 3 ? '⚡' : '○'} Transit Support {ad.transitAnalysis.transitSupport}
                      </Chip>
                    </Box>
                  </Box>
                )}
                {/* Life Focus Areas */}
                {ad.lifeFocusAreas && ad.lifeFocusAreas.length > 0 && (
                  <Box>
                    <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.5 }}>Life Focus Areas:</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {ad.lifeFocusAreas.slice(0, 6).map(area => (
                        <Chip key={area} size="sm" variant="soft" color="neutral">{area}</Chip>
                      ))}
                      {ad.lifeFocusAreas.length > 6 && (
                        <Chip size="sm" variant="outlined" color="neutral">+{ad.lifeFocusAreas.length - 6} more</Chip>
                      )}
                    </Box>
                  </Box>
                )}
              </Card>
            );
          })()}
        </Box>
      )}

      <Card
        variant="soft"
        sx={{
          p: 2.5,
          mb: 4,
          mt: 3,
          background: 'linear-gradient(135deg, rgba(91, 33, 182, 0.05), rgba(245, 158, 11, 0.05))',
        }}
      >
        <Typography level="title-md" sx={{ mb: 2, color: 'primary.main' }}>
          🎯 Current Dasha Interpretation
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
            <strong>Mahadasha ({dashas.current.MD.planet}):</strong> The overarching theme of your current
            life phase. This planet's significations, house placement, and aspects define the major areas
            of focus and karmic lessons during this {formatDate(dashas.current.MD.start)} to{' '}
            {formatDate(dashas.current.MD.end)} period.
          </Typography>
          <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
            <strong>Antardasha ({dashas.current.AD.planet}):</strong> The sub-period refines how the
            Mahadasha energy manifests. {dashas.current.AD.planet}'s relationship with{' '}
            {dashas.current.MD.planet} (friend, enemy, or neutral) determines the ease or challenge of
            this phase.
          </Typography>
          <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
            <strong>Pratyantar Dasha ({dashas.current.PD.planet}):</strong> The most granular level,
            indicating day-to-day themes and monthly cycles. This is where specific events and decisions
            crystallize within the broader dasha framework.
          </Typography>
        </Box>
      </Card>

      {/* Upcoming timeline */}
      {dashas.timeline && dashas.timeline.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography level="title-lg" sx={{ mb: 2, color: 'primary.main' }}>
            🔮 Upcoming Dasha Periods
          </Typography>
          <Card variant="outlined" sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {dashas.timeline.slice(0, 10).map((period, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    pb: 2,
                    borderBottom: index < 9 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <Box
                    sx={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      bgcolor: getPlanetColor(period.planet),
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                      {period.planet} {period.level}
                    </Typography>
                  </Box>
                  <Typography level="body-xs" sx={{ color: 'text.secondary', textAlign: 'right' }}>
                    {formatDate(period.start)} - {formatDate(period.end)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Box>
      )}

      {/* Understanding Dashas */}
      <Card variant="outlined" sx={{ p: 3, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
        <Typography level="title-sm" sx={{ mb: 1.5, color: 'primary.main' }}>
          💡 Understanding the Dasha System
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
            <strong>Hierarchy:</strong> MD (6-20 years) → AD (months to years) → PD (days to months) → SD
            (hours to days). Each level adds specificity to timing.
          </Typography>
          <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
            <strong>Planetary Lordship:</strong> Check which houses the dasha lord rules in your chart.
            Those life areas become prominent during that dasha period.
          </Typography>
          <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
            <strong>Transits:</strong> Dasha periods show potential. Transits (current planetary positions)
            trigger manifestation. Both must align for major life events.
          </Typography>
          <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
            <strong>Preparation:</strong> Knowing upcoming dashas allows you to prepare mentally,
            spiritually, and practically for changing life themes.
          </Typography>
        </Box>
      </Card>
    </ReportSection>
  );
};
